import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Send, CheckCircle2, ShieldCheck, Clock, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";
import { BRAND, BUSINESS_TYPES } from "@/lib/siteData";

const initial = { name: "", business: "", phone: "", type: "", message: "" };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DemoForm = () => {
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [hp, setHp] = useState(""); // honeypot spam trap

  const update = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // prevent duplicate submissions

    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name";
    if (!form.business.trim()) next.business = "Please enter your business name";
    if (!form.phone.trim()) next.phone = "Please enter your phone number";
    if (!form.type) next.type = "Please select a business type";
    if (Object.keys(next).length) {
      setErrors(next);
      toast.error("Please complete the required fields.");
      return;
    }

    setSubmitting(true);
    try {
      // Sends the request server-side via Resend. Stays on the page.
      await axios.post(`${API}/demo-request`, { ...form, company_website: hp });
      setSubmitted(true);
      setForm(initial);
      toast.success(
        "Thank you! We've received your demo request. We'll email you within one business day."
      );
    } catch (err) {
      toast.error("We couldn't send your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo" className="relative bg-background py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          {/* Left copy */}
          <Reveal dir="left">
            <span className="mb-4 inline-block rounded-full bg-accent px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent-foreground">
              Schedule a Demo
            </span>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Schedule Your Personalized Demo
            </h2>
            <p className="mt-5 text-base text-muted-foreground md:text-lg">
              Tell us about your business and we&rsquo;ll show you how Anchor Web Systems can help
              you manage your website without depending on a developer.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                [ShieldCheck, "A trusted platform for local businesses"],
                [Clock, "See how updates take seconds, not weeks"],
                [Sparkles, "A personalized tour for your industry"],
              ].map(([Icon, text]) => (
                <li key={text} className="flex items-center gap-3 text-sm font-medium text-navy">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  {text}
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-2xl border border-border bg-secondary/50 p-5">
              <p className="text-sm font-medium text-navy">Questions before scheduling?</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Email us anytime and we&rsquo;ll be happy to help.
              </p>
              <button
                type="button"
                onClick={() =>
                  document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })
                }
                className="mt-2 inline-flex items-center gap-2 break-all text-left font-display text-lg font-bold text-navy transition-colors hover:text-primary sm:text-xl"
              >
                <Mail className="h-5 w-5 shrink-0 text-primary" />
                {BRAND.email}
              </button>
            </div>
          </Reveal>

          {/* Right form */}
          <Reveal dir="right" delay={0.1}>
            <div className="relative rounded-3xl border border-border bg-card p-6 shadow-elegant sm:p-8">
              <div className="absolute -right-3 -top-3 hidden h-24 w-24 rounded-full bg-gradient-blue opacity-10 blur-2xl sm:block" />
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex min-h-[420px] flex-col items-center justify-center text-center"
                >
                  <div className="grid h-16 w-16 place-items-center rounded-2xl bg-gradient-blue shadow-blue">
                    <CheckCircle2 className="h-8 w-8 text-primary-foreground" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-bold text-navy">
                    Thank you!
                  </h3>
                  <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                    Thank you! We&rsquo;ve received your demo request. We&rsquo;ll review your
                    information and email you within one business day to schedule your
                    personalized demo.
                  </p>
                  <Button
                    type="button"
                    variant="cta"
                    size="lg"
                    className="mt-7"
                    onClick={() => {
                      setForm(initial);
                      setSubmitted(false);
                    }}
                  >
                    Submit another request
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  {/* Honeypot spam trap — hidden from real users */}
                  <div className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="company_website">Company Website</label>
                    <input
                      id="company_website"
                      name="company_website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={hp}
                      onChange={(e) => setHp(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-navy">Name</Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder="Jane Doe"
                        className="h-11 rounded-xl bg-background"
                        aria-invalid={!!errors.name}
                      />
                      {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="business" className="text-navy">Business Name</Label>
                      <Input
                        id="business"
                        value={form.business}
                        onChange={(e) => update("business", e.target.value)}
                        placeholder="SparkleClean Co."
                        className="h-11 rounded-xl bg-background"
                        aria-invalid={!!errors.business}
                      />
                      {errors.business && <p className="text-xs text-destructive">{errors.business}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="phone" className="text-navy">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="(209) 555-0142"
                        className="h-11 rounded-xl bg-background"
                        aria-invalid={!!errors.phone}
                      />
                      {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="type" className="text-navy">Business Type</Label>
                      <Select value={form.type} onValueChange={(v) => update("type", v)}>
                        <SelectTrigger id="type" className="h-11 rounded-xl bg-background" aria-invalid={!!errors.type}>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {BUSINESS_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.type && <p className="text-xs text-destructive">{errors.type}</p>}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="message" className="text-navy">Message</Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Tell us what you'd like to update on your website…"
                      className="min-h-[110px] rounded-xl bg-background"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="cta"
                    size="xl"
                    className="w-full"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending Request…
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" /> Request My Demo
                      </>
                    )}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    By requesting a demo you agree to be contacted about Anchor Web Systems.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
